import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../stores/AuthContext';

const VerifyEmail = ({ email, onBackClick }) => {
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const navigate = useNavigate();
  const { login } = useAuth();

  // Countdown timer for resend button
  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => setResendCountdown(resendCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCountdown]);

  const handleResendEmail = async () => {
    setResending(true);
    setResent(false);
    try {
      // Trigger resend by attempting signup again with same email
      const response = await fetch('/api/v1/auth/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password: '', username: '' }),
      });

      if (response.ok) {
        setResent(true);
        setResendCountdown(60);
      }
    } catch (error) {
      console.error('Error resending verification email:', error);
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center">
            <svg
              className="w-8 h-8 text-primary"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
          </div>
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold bg-gradient-to-r from-ctx-teal via-ctx-blue-light to-foreground bg-clip-text text-transparent">
          Verify your email
        </h2>
        <p className="mt-2 text-center text-sm text-muted-foreground">
          We've sent a confirmation link to
        </p>
        <p className="text-center text-sm font-medium text-foreground mt-1">{email}</p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-card border border-border py-8 px-4 shadow-xl sm:rounded-lg sm:px-10">
          <div className="space-y-6">
            <div className="bg-info/10 border border-info/50 rounded-lg p-4">
              <p className="text-sm text-foreground">
                Click the confirmation link in the email to verify your address and activate your account.
              </p>
            </div>

            <div className="space-y-2">
              <p className="text-sm text-muted-foreground text-center">
                Didn't receive the email?
              </p>
              <button
                onClick={handleResendEmail}
                disabled={resending || resendCountdown > 0}
                className="w-full py-2 px-4 border border-primary rounded-md shadow-sm text-sm font-medium text-primary bg-background hover:bg-primary/5 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {resending
                  ? 'Resending...'
                  : resendCountdown > 0
                  ? `Resend in ${resendCountdown}s`
                  : 'Resend confirmation email'}
              </button>
              {resent && (
                <p className="text-xs text-success text-center">
                  ✓ Verification email resent
                </p>
              )}
            </div>

            <div className="border-t border-border pt-6">
              <button
                onClick={onBackClick}
                className="w-full inline-flex justify-center py-2 px-4 border border-border rounded-md shadow-sm bg-background text-sm font-medium text-muted-foreground hover:bg-accent hover:text-foreground"
              >
                Back to sign up
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
